'use client';

import { useMemo } from 'react';
import { useSearchParams } from 'next/navigation';

// UI state params that should not affect data query keys.
// NOTE: this deny-list is a stopgap. Any URL param NOT listed here becomes part
// of every table's query key on the same page (via searchKey). The proper fix is
// for tables to key off the params they actually declare (filterConfig keys +
// sorting/cursor params) — see resolveTableQueryKey in createTableSuspenseWrapper
// and useSuspenseList, which now default to a sanitized key for exactly this reason.
const UI_STATE_PARAMS = new Set(['sheet', 'sheetProps', 'modal', 'modalProps', 'announcement']);

/**
 * Custom hook to process search params into query key and typed object
 *
 * @template T - The expected type of the search params object
 * @param options - Configuration options
 * @param options.sortEntries - Whether to sort entries for consistent query keys (default: false)
 * @returns Object containing the search params as an object and a URL-safe query key
 *
 * @example
 * ```tsx
 * const { searchParamsObject, searchKey } = useSearchParamsQuery<MyQueryParams>();
 *
 * // For sorted query keys (useful for caching):
 * const { searchParamsObject, searchKey } = useSearchParamsQuery<MyQueryParams>({
 *   sortEntries: true
 * });
 * ```
 */
export function useSearchParamsQuery<T extends object>(options: { sortEntries?: boolean } = {}) {
  const { sortEntries = false } = options;
  const searchParams = useSearchParams();

  return useMemo(() => {
    const entries = Array.from(searchParams?.entries() ?? []);

    // Filter out UI state params (sheet/modal) that shouldn't affect data queries
    const dataEntries = entries.filter(([key]) => !UI_STATE_PARAMS.has(key));

    // Sort entries if requested for consistent query keys
    const processedEntries = sortEntries ? dataEntries.sort(([a], [b]) => a.localeCompare(b)) : dataEntries;

    const searchParamsObject = Object.fromEntries(processedEntries) as T;
    const searchKey = new URLSearchParams(processedEntries).toString();

    return {
      searchParamsObject,
      searchKey,
    };
  }, [searchParams, sortEntries]);
}
